import { createContext, useCallback, useContext, useState } from "react";
import { useRouter } from "@/lib/router";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api/http";

type CreationState =
  | { status: "idle" }
  | { status: "creating" }
  | { status: "error"; message: string };

type NoteCreationContextValue = {
  state: CreationState;
  startUpload: (file: File) => void;
};

const NoteCreationContext = createContext<NoteCreationContextValue | null>(null);

/**
 * 업로드 모달은 "확인" 누르면 바로 닫힌다 — 근데 업로드+분석은 계속
 * 진행돼야 하므로, 그 상태를 모달(닫히면 사라질 컴포넌트) 밖으로 뺐다.
 * (app-pages)/layout.tsx에서 AppShell을 이걸로 감싸서, 사이드바·홈 화면
 * 어느 쪽 버튼으로 시작해도 우측 하단 토스트가 같은 자리에 뜨고, 끝나면
 * 자동으로 그 노트로 이동한다.
 */
export function NoteCreationProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [state, setState] = useState<CreationState>({ status: "idle" });

  const startUpload = useCallback(
    (file: File) => {
      setState({ status: "creating" });

      const formData = new FormData();
      formData.append("image", file);

      apiFetch("/api/notes", { method: "POST", body: formData })
        .then(async (res) => {
          // 플랫폼(Vercel)이 413을 돌려주면 본문이 JSON이 아니라서 json()이 실패한다.
          const data = await res.json().catch(() => null);
          if (!res.ok) {
            const message =
              res.status === 413
                ? "사진이 너무 커요. 4MB 이하의 사진으로 올려주세요."
                : (data?.error ?? "업로드 중 오류가 발생했습니다.");
            setState({ status: "error", message });
            return;
          }
          setState({ status: "idle" });
          router.push(`/notes/${data.note.id}`);
        })
        .catch(() => {
          setState({ status: "error", message: "업로드 중 오류가 발생했습니다." });
        });
    },
    [router],
  );

  return (
    <NoteCreationContext.Provider value={{ state, startUpload }}>
      {children}
      {state.status === "creating" ? (
        <div className="fixed bottom-20 right-4 z-50 flex items-center gap-2 rounded-lg border border-border bg-popover px-4 py-3 text-sm shadow-lg">
          <Loader2 className="size-4 animate-spin" />
          설교 노트를 만드는 중...
        </div>
      ) : null}
      {state.status === "error" ? (
        <div className="fixed bottom-20 right-4 z-50 flex items-center gap-3 rounded-lg border border-destructive/30 bg-popover px-4 py-3 text-sm text-destructive shadow-lg">
          {state.message}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setState({ status: "idle" })}
            aria-label="닫기"
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : null}
    </NoteCreationContext.Provider>
  );
}

export function useNoteCreation() {
  const ctx = useContext(NoteCreationContext);
  if (!ctx) {
    throw new Error("useNoteCreation은 NoteCreationProvider 안에서만 쓸 수 있습니다.");
  }
  return ctx;
}
